import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table, Tag, Drawer, Collapse, Tabs, Switch } from "antd";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip } from "recharts";
import { Link } from "react-router-dom";
import { EditInfo, DeleteModal } from ".";
import { getPlatformsList, patchPlatformStatus } from "../../appRedux/actions/platforms";
import SubscriptionListView from "../Subscriptions/ListView";

const KNOWN_PLATFORM_ENRICHMENTS = {
  udemy: {
    name: "Udemy",
    website: "https://www.udemy.com",
    description: "Leading global marketplace for online learning with over 210,000 courses in development, business, design, IT, and personal growth.",
    logoBg: "#a435f0",
    initials: "UD",
    pricingModel: "Pay-Per-Course & Subscriptions",
    affiliateProgram: "Rakuten Advertising (10-15% Comm)",
    focusAreas: ["Tech & Dev", "Business", "Design", "Marketing"],
    certificatesOffered: "Certificates of Completion",
    headquarters: "San Francisco, CA",
    foundedYear: "2010",
    enrichmentStatus: "ENRICHED",
  },
  coursera: {
    name: "Coursera",
    website: "https://www.coursera.org",
    description: "World-class learning platform partnering with 300+ top universities and companies to offer university degrees, professional certificates, and courses.",
    logoBg: "#0056D2",
    initials: "C",
    pricingModel: "Coursera Plus & Single Purchase",
    affiliateProgram: "Impact.com (10-45% Comm)",
    focusAreas: ["Data Science", "Computer Science", "Business", "Health"],
    certificatesOffered: "Professional Certs & Degrees",
    headquarters: "Mountain View, CA",
    foundedYear: "2012",
    enrichmentStatus: "ENRICHED",
  },
  edx: {
    name: "edX",
    website: "https://www.edx.org",
    description: "Massive open online course provider created by Harvard and MIT, offering high-quality courses from top universities worldwide.",
    logoBg: "#02262B",
    initials: "eX",
    pricingModel: "Free Audit & Verified Certs",
    affiliateProgram: "Awin / Direct (10-15% Comm)",
    focusAreas: ["Computer Science", "Humanities", "Engineering", "AI"],
    certificatesOffered: "MicroMasters & Verified Certs",
    headquarters: "Cambridge, MA",
    foundedYear: "2012",
    enrichmentStatus: "ENRICHED",
  },
  codecademy: {
    name: "Codecademy",
    website: "https://www.codecademy.com",
    description: "Interactive online learning platform offering free coding classes in 12 programming languages including Python, Java, and JavaScript.",
    logoBg: "#1F293D",
    initials: "CA",
    pricingModel: "Codecademy Pro Subscription",
    affiliateProgram: "CJ Affiliate (15% Comm)",
    focusAreas: ["Web Dev", "Data Science", "Computer Science"],
    certificatesOffered: "Professional Certificates",
    headquarters: "New York, NY",
    foundedYear: "2011",
    enrichmentStatus: "ENRICHED",
  },
  pluralsight: {
    name: "Pluralsight",
    website: "https://www.pluralsight.com",
    description: "Technology workforce development company providing tech skill assessments and online training courses for software developers and IT admins.",
    logoBg: "#F15B2A",
    initials: "PS",
    pricingModel: "Individual & Team Subscriptions",
    affiliateProgram: "Impact.com (15% Comm)",
    focusAreas: ["Software Dev", "IT Ops", "Cybersecurity", "Cloud"],
    certificatesOffered: "Skill Assessments & Certs",
    headquarters: "Draper, UT",
    foundedYear: "2004",
    enrichmentStatus: "ENRICHED",
  },
};

const getEnrichedPlatformDetails = (record) => {
  const code = String(record?.code || "").trim().toLowerCase();
  const defaults = KNOWN_PLATFORM_ENRICHMENTS[code] || {};
  const meta = record?.extendedMetadata || {};

  const website = meta.website || defaults.website || "";
  const description = record?.description || meta.description || defaults.description || "";
  const pricingModel = meta.pricingModel || defaults.pricingModel || "Standard Pricing";
  const affiliateProgram = meta.affiliateProgram || defaults.affiliateProgram || "Direct / Rakuten";
  const focusAreas = meta.focusAreas || defaults.focusAreas || ["General Learning"];
  const certificatesOffered = meta.certificatesOffered || defaults.certificatesOffered || "Certificate of Completion";
  const headquarters = meta.headquarters || defaults.headquarters || "Global";
  const foundedYear = meta.foundedYear || defaults.foundedYear || "—";
  const logoBg = defaults.logoBg || "#4F46E5";
  const initials = defaults.initials || String(record?.name || "P").substring(0, 2).toUpperCase();

  const isEnriched = meta.enrichmentStatus === "ENRICHED" || defaults.enrichmentStatus === "ENRICHED" || Boolean(website && description);

  return {
    website,
    description,
    pricingModel,
    affiliateProgram,
    focusAreas: Array.isArray(focusAreas) ? focusAreas : [focusAreas],
    certificatesOffered,
    headquarters,
    foundedYear,
    logoBg,
    initials,
    isEnriched,
    enrichmentStatus: isEnriched ? "ENRICHED" : "BASIC",
  };
};

const getPlatformMockAnalytics = (code) => {
  const cleanCode = String(code || "").trim().toLowerCase();
  switch (cleanCode) {
    case "udemy":
      return {
        courses: 412,
        revenue: "₹1,84,300",
        clicks: 8420,
        conversionRate: "4.8%",
        chartData: [
          { name: "Week 1", Clicks: 1200, Impressions: 24000 },
          { name: "Week 2", Clicks: 1900, Impressions: 32000 },
          { name: "Week 3", Clicks: 2400, Impressions: 38000 },
          { name: "Week 4", Clicks: 2920, Impressions: 45000 },
        ],
        topCourses: [
          { title: "Complete Web Developer in 2026", clicks: 1450, sales: 82, conversion: "5.6%" },
          { title: "React - The Complete Guide", clicks: 1220, sales: 65, conversion: "5.3%" },
          { title: "Python Bootcamp Zero to Hero", clicks: 980, sales: 44, conversion: "4.5%" },
        ],
        insight: "Udemy Outbound clicks peaked on weekends. Outbound clicks on Development categories are up by 15%. Consider promoting Udemy coupon codes on the landing page.",
      };
    case "coursera":
      return {
        courses: 184,
        revenue: "₹4,12,000",
        clicks: 5210,
        conversionRate: "7.2%",
        chartData: [
          { name: "Week 1", Clicks: 900, Impressions: 18000 },
          { name: "Week 2", Clicks: 1100, Impressions: 21000 },
          { name: "Week 3", Clicks: 1500, Impressions: 26000 },
          { name: "Week 4", Clicks: 1710, Impressions: 29000 },
        ],
        topCourses: [
          { title: "Google Data Analytics Professional Certificate", clicks: 920, sales: 74, conversion: "8.0%" },
          { title: "AI For Everyone by Andrew Ng", clicks: 750, sales: 58, conversion: "7.7%" },
          { title: "Python for Everybody Specialization", clicks: 610, sales: 41, conversion: "6.7%" },
        ],
        insight: "Coursera Plus subscription clicks have a 7.2% conversion rate, compared to 2.8% for individual courses. Focus on marketing the Coursera Plus annual pricing option.",
      };
    case "edx":
    default:
      return {
        courses: 95,
        revenue: "₹92,400",
        clicks: 2840,
        conversionRate: "3.5%",
        chartData: [
          { name: "Week 1", Clicks: 500, Impressions: 12000 },
          { name: "Week 2", Clicks: 650, Impressions: 14000 },
          { name: "Week 3", Clicks: 790, Impressions: 15500 },
          { name: "Week 4", Clicks: 900, Impressions: 17000 },
        ],
        topCourses: [
          { title: "Harvard CS50's Introduction to Computer Science", clicks: 450, sales: 21, conversion: "4.6%" },
          { title: "MITx Introduction to Computer Science and Programming", clicks: 310, sales: 12, conversion: "3.8%" },
          { title: "Data Science Specialization by HarvardX", clicks: 240, sales: 8, conversion: "3.3%" },
        ],
        insight: "Outbound affiliate clicks on Professional Certificates remain high but signups are slow. Recommend adding dynamic discounts or highlighting trials.",
      };
  }
};

function ListView() {
  const dispatch = useDispatch();
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const { platformList, queryInfo, totalRecords } = useSelector(
    (state) => ({
      platformList: state.platforms.platformList,
      queryInfo: state.platforms.queryInfo,
      totalRecords: state.platforms.totalRecords,
    })
  );

  useEffect(() => {
    if (selectedPlatform) {
      document.body.classList.add("hide-sidebar-hub");
    } else {
      document.body.classList.remove("hide-sidebar-hub");
    }
    return () => {
      document.body.classList.remove("hide-sidebar-hub");
    };
  }, [selectedPlatform]);

  const handleTableChange = (pagination) => {
    const { current, pageSize } = pagination;
    dispatch(
      getPlatformsList({
        ...queryInfo,
        page: current,
        size: pageSize,
      })
    );
  };

  const columns = [
    {
      title: "Platform & Code",
      dataIndex: "name",
      align: "left",
      width: "32%",
      render: (name, record) => {
        const enriched = getEnrichedPlatformDetails(record);
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div className="platform-brand-avatar" style={{ backgroundColor: enriched.logoBg }}>
              {enriched.initials}
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span 
                onClick={() => setSelectedPlatform(record)} 
                style={{ color: "#1890ff", cursor: "pointer", fontWeight: 600, fontSize: "14px" }}
                className="hover-text-primary"
              >
                {name}
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "2px" }}>
                <Tag color="purple" style={{ margin: 0, borderRadius: "4px", fontSize: "11px", fontWeight: 600 }}>
                  {record.code}
                </Tag>
                {enriched.website && (
                  <span style={{ fontSize: "11px", color: "#6b7280" }}>
                    {enriched.website.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "")}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      title: "Enrichment Status",
      key: "enrichmentStatus",
      align: "center",
      width: "18%",
      render: (_, record) => {
        const enriched = getEnrichedPlatformDetails(record);
        return enriched.isEnriched ? (
          <Tag color="success" style={{ borderRadius: "4px", padding: "3px 10px", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: "4px" }}>
            <i className="fa fa-check-circle" /> Enriched
          </Tag>
        ) : (
          <Tag color="default" style={{ borderRadius: "4px", padding: "3px 10px", color: "#6b7280", display: "inline-flex", alignItems: "center", gap: "4px" }}>
            <i className="fa fa-info-circle" /> Basic
          </Tag>
        );
      },
    },
    {
      title: "Focus & Tags",
      key: "metaTags",
      align: "left",
      width: "25%",
      render: (_, record) => {
        const enriched = getEnrichedPlatformDetails(record);
        const tags = Array.isArray(record.tags) ? record.tags : [];
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
              {tags.length > 0 ? (
                tags.slice(0, 3).map((tag, idx) => (
                  <Tag
                    key={tag._id || idx}
                    style={{
                      margin: 0,
                      borderRadius: "4px",
                      fontSize: "11px",
                      backgroundColor: tag.color ? `${tag.color}15` : "#eef2ff",
                      color: tag.color || "#4f46e5",
                      borderColor: tag.color ? `${tag.color}40` : "#c7d2fe",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "3px",
                    }}
                  >
                    {tag.icon && <i className={tag.icon} style={{ fontSize: "11px" }} />}
                    <span>{typeof tag === "object" ? tag.name : tag}</span>
                  </Tag>
                ))
              ) : (
                enriched.focusAreas.slice(0, 2).map((area, idx) => (
                  <Tag key={idx} color="geekblue" style={{ margin: 0, borderRadius: "4px", fontSize: "11px" }}>
                    {area}
                  </Tag>
                ))
              )}
              {tags.length > 3 && (
                <Tag style={{ margin: 0, borderRadius: "4px", fontSize: "10px" }}>
                  +{tags.length - 3}
                </Tag>
              )}
            </div>
            <span style={{ fontSize: "11px", color: "#4b5563", fontWeight: 500 }}>
              <i className="fa fa-tag text-muted mr-1" /> {enriched.pricingModel}
            </span>
          </div>
        );
      },
    },
    {
      title: "Synced Courses",
      dataIndex: "totalCourses",
      align: "center",
      width: "12%",
      render: (totalCourses) => (
        <Tag color="cyan" style={{ borderRadius: "4px", padding: "3px 10px", fontWeight: 600, fontSize: "12px" }}>
          <i className="fa fa-book mr-1" /> {totalCourses || 0}
        </Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      align: "center",
      width: "13%",
      render: (isActive, record) => (
        <div style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
          <Switch
            size="small"
            checked={isActive}
            onChange={(checked) => {
              dispatch(patchPlatformStatus(record._id, checked))
                .then(() => dispatch(getPlatformsList(queryInfo)).catch(() => {}))
                .catch(() => {});
            }}
          />
          <Tag color={isActive ? "success" : "error"} style={{ borderRadius: "4px", margin: 0 }}>
            {isActive ? "Active" : "Inactive"}
          </Tag>
        </div>
      ),
    },
    {
      title: "Action",
      align: "center",
      width: "10%",
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
          <Link
            to={`/platforms/builder/${record._id}`}
            title="Open Platform PageBuilder"
            style={{
              width: "28px",
              height: "28px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "4px",
              backgroundColor: "#f0fdf4",
              border: "1px solid #bbf7d0",
              color: "#16a34a",
              cursor: "pointer",
            }}
          >
            <i className="fa fa-magic" />
          </Link>
          <button
            className="offer-preview-btn"
            onClick={() => setSelectedPlatform(record)}
            title="Open Platform Hub"
            style={{ width: "28px", height: "28px", border: "none", background: "transparent", cursor: "pointer", color: "#1890ff" }}
          >
            <i className="fa fa-eye" />
          </button>
          <div className="dropdown dropdown-action text-center">
            <Link
              to="#"
              className="action-icon dropdown-toggle"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="material-icons">more_vert</i>
            </Link>
            <div className="dropdown-menu dropdown-menu-right">
              <Link to={`/platforms/builder/${record._id}`} className="dropdown-item">
                <i className="fa fa-magic m-r-5 text-primary" /> PageBuilder Studio
              </Link>
              <button className="dropdown-item" onClick={() => setSelectedPlatform(record)}>
                <i className="fa fa-eye m-r-5" /> Open Hub
              </button>
              <EditInfo info={record} key={"edit_info"} />
              <DeleteModal info={record} />
            </div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="row m-0 mt-3">
      <div className="col-md-12 p-0" id="table-responsive">
        <div className="table-responsive" id="setMinheight">
          <Table
            className="table-striped platform-hub-table"
            pagination={{
              total: totalRecords,
              pageSizeOptions: ["5", "10", "20", "50"],
              showSizeChanger: true,
              showTotal: (total, range) => `Showing ${range[0]} to ${range[1]} of ${total} platforms`,
            }}
            onChange={handleTableChange}
            style={{ overflowX: "auto" }}
            columns={columns}
            dataSource={platformList}
            rowKey={(record) => record._id}
          />
        </div>
      </div>

      <Drawer
        title={
          selectedPlatform && (() => {
            const enriched = getEnrichedPlatformDetails(selectedPlatform);
            return (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", paddingRight: "24px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <div className="platform-brand-avatar" style={{ backgroundColor: enriched.logoBg, width: "40px", height: "40px", fontSize: "16px" }}>
                    {enriched.initials}
                  </div>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span style={{ fontSize: "20px", fontWeight: 700, color: "#111827" }}>{selectedPlatform.name}</span>
                      <Tag color="purple" style={{ borderRadius: "4px", margin: 0, fontWeight: 600 }}>{selectedPlatform.code}</Tag>
                      {enriched.isEnriched ? (
                        <Tag color="success" style={{ borderRadius: "4px", margin: 0, fontWeight: 600 }}>
                          <i className="fa fa-check-circle mr-1" /> Enriched
                        </Tag>
                      ) : (
                        <Tag color="default" style={{ borderRadius: "4px", margin: 0 }}>
                          <i className="fa fa-info-circle mr-1" /> Basic
                        </Tag>
                      )}
                    </div>
                    <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "2px" }}>
                      {enriched.description.length > 90 ? `${enriched.description.substring(0, 90)}...` : enriched.description}
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Link
                    to={`/platforms/builder/${selectedPlatform._id}`}
                    className="btn btn-sm btn-primary"
                    style={{ borderRadius: "6px", display: "inline-flex", alignItems: "center", gap: "6px", fontWeight: 500 }}
                  >
                    <i className="fa fa-magic" /> PageBuilder Studio
                  </Link>
                  {enriched.website && (
                    <a
                      href={enriched.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-sm btn-outline-primary"
                      style={{ borderRadius: "6px", display: "inline-flex", alignItems: "center", gap: "6px" }}
                    >
                      Visit Website <i className="fa fa-external-link" />
                    </a>
                  )}
                </div>
              </div>
            );
          })()
        }
        placement="right"
        width="100%"
        zIndex={1000}
        onClose={() => setSelectedPlatform(null)}
        open={!!selectedPlatform}
      >
        {selectedPlatform && (() => {
          const analytics = getPlatformMockAnalytics(selectedPlatform.code);
          const enriched = getEnrichedPlatformDetails(selectedPlatform);

          return (
            <Tabs defaultActiveKey="1" style={{ marginTop: "-12px" }}>
              <Tabs.TabPane tab={<span><i className="fa fa-info-circle mr-1"/> Overview & Metadata</span>} key="1">
                <div style={{ marginTop: "16px" }}>
                  {/* Enrichment Health & Completeness Banner */}
                  <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "14px 18px", marginBottom: "20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div>
                      <h4 style={{ margin: 0, fontSize: "14px", fontWeight: 600, color: "#1e293b" }}>Platform Metadata Completeness</h4>
                      <p style={{ margin: "2px 0 0", fontSize: "12px", color: "#64748b" }}>
                        {enriched.isEnriched ? "Fully enriched platform profile with verified affiliate & monetization data." : "Basic platform profile. Add metadata in Edit Platform to enrich."}
                      </p>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <Tag color={enriched.isEnriched ? "success" : "warning"} style={{ borderRadius: "4px", padding: "4px 12px", fontWeight: 600, fontSize: "13px" }}>
                        {enriched.isEnriched ? "100% Enriched" : "Basic Profile"}
                      </Tag>
                    </div>
                  </div>

                  {/* 3-Column Enriched Metadata Grid */}
                  <div className="row g-3" style={{ marginBottom: "24px" }}>
                    {/* General Info Card */}
                    <div className="col-12 col-md-4">
                      <div className="platform-meta-card">
                        <div className="platform-meta-card-title">
                          <i className="fa fa-building text-primary" /> General Platform Info
                        </div>
                        <div className="platform-meta-item">
                          <span className="platform-meta-label">Official Website:</span>
                          <span className="platform-meta-val">
                            {enriched.website ? (
                              <a href={enriched.website} target="_blank" rel="noopener noreferrer" style={{ color: "#2563eb" }}>
                                {enriched.website.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "")}
                              </a>
                            ) : (
                              "Not linked"
                            )}
                          </span>
                        </div>
                        <div className="platform-meta-item">
                          <span className="platform-meta-label">Headquarters:</span>
                          <span className="platform-meta-val">{enriched.headquarters}</span>
                        </div>
                        <div className="platform-meta-item">
                          <span className="platform-meta-label">Founded Year:</span>
                          <span className="platform-meta-val">{enriched.foundedYear}</span>
                        </div>
                        <div className="platform-meta-item">
                          <span className="platform-meta-label">Active Status:</span>
                          <span className="platform-meta-val">
                            <Tag color={selectedPlatform.isActive ? "success" : "error"} style={{ borderRadius: "4px", margin: 0 }}>
                              {selectedPlatform.isActive ? "Active" : "Inactive"}
                            </Tag>
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Monetization & Affiliate Card */}
                    <div className="col-12 col-md-4">
                      <div className="platform-meta-card">
                        <div className="platform-meta-card-title">
                          <i className="fa fa-dollar-sign text-success" /> Monetization & Affiliate
                        </div>
                        <div className="platform-meta-item">
                          <span className="platform-meta-label">Pricing Model:</span>
                          <span className="platform-meta-val">{enriched.pricingModel}</span>
                        </div>
                        <div className="platform-meta-item">
                          <span className="platform-meta-label">Affiliate Network:</span>
                          <span className="platform-meta-val">{enriched.affiliateProgram}</span>
                        </div>
                        <div className="platform-meta-item">
                          <span className="platform-meta-label">Total Synced Courses:</span>
                          <span className="platform-meta-val">{selectedPlatform.totalCourses || 0}</span>
                        </div>
                        <div className="platform-meta-item">
                          <span className="platform-meta-label">Integration:</span>
                          <span className="platform-meta-val">
                            <Tag color="cyan" style={{ borderRadius: "4px", margin: 0 }}>API & Affiliate</Tag>
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Catalog & Credentials Card */}
                    <div className="col-12 col-md-4">
                      <div className="platform-meta-card">
                        <div className="platform-meta-card-title">
                          <i className="fa fa-graduation-cap text-purple" /> Catalog & Credentials
                        </div>
                        <div className="platform-meta-item">
                          <span className="platform-meta-label">Certificates:</span>
                          <span className="platform-meta-val">{enriched.certificatesOffered}</span>
                        </div>
                        <div className="platform-meta-item">
                          <span className="platform-meta-label">App Support:</span>
                          <span className="platform-meta-val">Web, iOS, Android</span>
                        </div>
                        <div className="platform-meta-item" style={{ flexDirection: "column", alignItems: "flex-start", gap: "6px" }}>
                          <span className="platform-meta-label">Focus Areas:</span>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                            {enriched.focusAreas.map((area, i) => (
                              <Tag key={i} color="geekblue" style={{ margin: 0, borderRadius: "4px", fontSize: "11px" }}>
                                {area}
                              </Tag>
                            ))}
                          </div>
                        </div>

                        {Array.isArray(selectedPlatform.tags) && selectedPlatform.tags.length > 0 && (
                          <div className="platform-meta-item" style={{ flexDirection: "column", alignItems: "flex-start", gap: "6px" }}>
                            <span className="platform-meta-label">Decision & Comparison Tags:</span>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                              {selectedPlatform.tags.map((tag, i) => (
                                <Tag
                                  key={tag._id || i}
                                  style={{
                                    margin: 0,
                                    borderRadius: "4px",
                                    fontSize: "11px",
                                    backgroundColor: tag.color ? `${tag.color}15` : "#eef2ff",
                                    color: tag.color || "#4f46e5",
                                    borderColor: tag.color ? `${tag.color}40` : "#c7d2fe",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    fontWeight: 500,
                                  }}
                                >
                                  {tag.icon && <i className={tag.icon} style={{ fontSize: "11px" }} />}
                                  <span>{typeof tag === "object" ? tag.name : tag}</span>
                                  {tag.group && (
                                    <span style={{ fontSize: "9px", opacity: 0.8 }}>({tag.group})</span>
                                  )}
                                </Tag>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Global Platform FAQs Accordion */}
                  <h4 style={{ fontWeight: 600, marginBottom: "12px", color: "#374151", fontSize: "15px", display: "flex", alignItems: "center", gap: "6px" }}>
                    <i className="fa fa-question-circle text-primary" /> Global Platform FAQs
                  </h4>
                  {selectedPlatform.faqs && selectedPlatform.faqs.length > 0 ? (
                    <Collapse accordion style={{ background: "#f9fafb", borderRadius: "8px" }}>
                      {selectedPlatform.faqs.map((faq, idx) => (
                        <Collapse.Panel 
                          header={
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", paddingRight: "16px" }}>
                              <span style={{ fontWeight: 500 }}>{faq.question}</span>
                              {!faq.isActive && <Tag style={{ margin: 0 }}>Inactive</Tag>}
                            </div>
                          } 
                          key={idx}
                        >
                          <p style={{ margin: 0, color: "#4b5563", lineHeight: 1.5 }}>{faq.answer}</p>
                          {faq.priority ? <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "8px" }}>Priority: {faq.priority}</div> : null}
                        </Collapse.Panel>
                      ))}
                    </Collapse>
                  ) : (
                    <div style={{ padding: "24px 0", textAlign: "center", color: "#9ca3af", background: "#f9fafb", borderRadius: "8px", border: "1px dashed #e5e7eb" }}>
                      No FAQs defined at platform level.
                    </div>
                  )}
                </div>
              </Tabs.TabPane>

              <Tabs.TabPane tab={<span><i className="fa fa-tags mr-1"/> Subscriptions</span>} key="2">
                <div style={{ marginTop: "16px" }}>
                  <SubscriptionListView merchantCode={selectedPlatform.code} />
                </div>
              </Tabs.TabPane>

              <Tabs.TabPane tab={<span><i className="fa fa-chart-bar mr-1"/> Analytics & Insights</span>} key="3">
                <div style={{ marginTop: "16px" }}>
                  {/* KPI Cards */}
                  <div className="row g-2" style={{ marginBottom: "20px" }}>
                    <div className="col-6 col-md-3">
                      <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "6px", padding: "10px", textAlign: "center" }}>
                        <div style={{ fontSize: "12px", color: "#6b7280" }}>Synced Courses</div>
                        <h4 style={{ margin: "4px 0 0 0", fontWeight: 700, color: "#111827" }}>{selectedPlatform.totalCourses || analytics.courses}</h4>
                      </div>
                    </div>
                    <div className="col-6 col-md-3">
                      <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "6px", padding: "10px", textAlign: "center" }}>
                        <div style={{ fontSize: "12px", color: "#6b7280" }}>Outbound Clicks</div>
                        <h4 style={{ margin: "4px 0 0 0", fontWeight: 700, color: "#2563eb" }}>{analytics.clicks}</h4>
                      </div>
                    </div>
                    <div className="col-6 col-md-3">
                      <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "6px", padding: "10px", textAlign: "center" }}>
                        <div style={{ fontSize: "12px", color: "#6b7280" }}>Est. Revenue</div>
                        <h4 style={{ margin: "4px 0 0 0", fontWeight: 700, color: "#059669" }}>{analytics.revenue}</h4>
                      </div>
                    </div>
                    <div className="col-6 col-md-3">
                      <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "6px", padding: "10px", textAlign: "center" }}>
                        <div style={{ fontSize: "12px", color: "#6b7280" }}>Conversion Rate</div>
                        <h4 style={{ margin: "4px 0 0 0", fontWeight: 700, color: "#d97706" }}>{analytics.conversionRate}</h4>
                      </div>
                    </div>
                  </div>

                  {/* Chart */}
                  <div style={{ border: "1px solid #e5e7eb", borderRadius: "8px", padding: "16px", background: "#fff", marginBottom: "20px" }}>
                    <h5 style={{ fontWeight: 600, fontSize: "13px", color: "#374151", marginBottom: "12px" }}>Outbound Clickstream & Impressions Trend (Last 30 Days)</h5>
                    <ResponsiveContainer width="100%" height={180}>
                      <AreaChart data={analytics.chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                        <defs>
                          <linearGradient id="colorClicks" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#2563eb" stopOpacity={0.2}/>
                            <stop offset="95%" stopColor="#2563eb" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" style={{ fontSize: "10px" }} />
                        <YAxis style={{ fontSize: "10px" }} />
                        <ChartTooltip />
                        <Area type="monotone" dataKey="Clicks" stroke="#2563eb" fillOpacity={1} fill="url(#colorClicks)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Top Courses Leaderboard */}
                  <h5 style={{ fontWeight: 600, fontSize: "13px", color: "#374151", marginBottom: "12px" }}>Top Performing Courses</h5>
                  <div className="table-responsive" style={{ border: "1px solid #e5e7eb", borderRadius: "6px", marginBottom: "20px" }}>
                    <table className="table table-sm" style={{ margin: 0, fontSize: "12px" }}>
                      <thead>
                        <tr style={{ background: "#f9fafb" }}>
                          <th>Course Title</th>
                          <th style={{ textAlign: "center" }}>Clicks</th>
                          <th style={{ textAlign: "center" }}>Sales</th>
                          <th style={{ textAlign: "center" }}>CTR</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.topCourses.map((c, i) => (
                          <tr key={i}>
                            <td style={{ maxWidth: "220px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={c.title}>
                              {c.title}
                            </td>
                            <td style={{ textAlign: "center", fontWeight: 600, color: "#2563eb" }}>{c.clicks}</td>
                            <td style={{ textAlign: "center", fontWeight: 600, color: "#059669" }}>{c.sales}</td>
                            <td style={{ textAlign: "center", color: "#d97706" }}>{c.conversion}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* AI/MS Clarity Strategic Insight */}
                  <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "8px", padding: "14px", display: "flex", gap: "10px" }}>
                    <i className="fa fa-info-circle" style={{ color: "#2563eb", fontSize: "18px", marginTop: "2px" }} />
                    <div>
                      <strong style={{ display: "block", fontSize: "12px", color: "#1e3a8a", marginBottom: "4px" }}>Strategic Recommendation:</strong>
                      <p style={{ margin: 0, fontSize: "12px", color: "#1e40af", lineHeight: 1.5 }}>
                        {analytics.insight}
                      </p>
                    </div>
                  </div>
                </div>
              </Tabs.TabPane>
            </Tabs>
          );
        })()}
      </Drawer>
    </div>
  );
}

export default ListView;
